import type { MentionContextType, Service } from '../graphqlEnums'

/**
 * Prefix label ("Mentioned you in their {label}"). Adds `'video'` to
 * `MentionContextType` for YouTube post mentions. FE-only.
 */
export type MentionSubjectLabel = MentionContextType | 'video'

/**
 * Structural channel shape the mentions capability bundle reads. Kept
 * loose so this file doesn't depend on `CommunityConfigContext.Channel`
 * — any object with `service` satisfies it (including all `Channel`
 * variants).
 */
export type MentionsCapabilityChannel = { service: Service }

/**
 * Per-channel capability bundle for the Mentions inbox. Every predicate here
 * reads the static `MENTIONS_SUPPORT_BY_SERVICE` table below, keyed by service.
 *
 * Reactions are not part of the bundle: they come from the configuration
 * endpoint, via `getEngagementReactions` on the Community config.
 *
 * TODO: source `isSupported`, `canReply` and `showPrefix` from
 * `MentionsConfiguration` too. `hasFullContext`, `subjectLabel` and
 * `deepLinkLabel` have no backend counterpart and stay here.
 */
export type MentionsCapabilityConfig = {
  isSupported: (channel: MentionsCapabilityChannel) => boolean
  canReply: (
    channel: MentionsCapabilityChannel,
    mentionType: MentionContextType,
  ) => boolean
  hasFullContext: (
    channel: MentionsCapabilityChannel,
    mentionType: MentionContextType,
  ) => boolean
  /**
   * `true` when the service never supports replying to ANY mentionType
   * — Buffer can only notify the user about the mention. Today this is
   * Facebook. Use this (rather than per-mentionType
   * `canReply`) when the card needs notify-only-specific layout (e.g.
   * the disclaimer body that replaces normal content).
   */
  isNotifyOnly: (channel: MentionsCapabilityChannel) => boolean
  subjectLabel: (
    channel: MentionsCapabilityChannel,
    mentionType: MentionContextType,
  ) => MentionSubjectLabel
  showPrefix: (channel: MentionsCapabilityChannel) => boolean
  /**
   * Copy for the deep-link button on partial-experience cards.
   * (Facebook → "Open Thread", YouTube → "Go to YouTube")
   */
  deepLinkLabel: (channel: MentionsCapabilityChannel) => string
}

// Per-service mentions capabilities. Services missing from this map are
// not surfaced in the Mentions inbox; add an entry to opt one in.
// `NOTIFY_ONLY` is the shape for services where Buffer can't reply on
// the user's behalf (Facebook).

type MentionsServiceSupport = {
  canReply: ReadonlyArray<MentionContextType>
  hasFullContext: ReadonlyArray<MentionContextType>
  showPrefix: boolean
  isNotifyOnly?: boolean
  /** Per-type overrides for the prefix label. Defaults to `mentionType`. */
  subjectLabels?: Partial<Record<MentionContextType, MentionSubjectLabel>>
  /** Deep-link button copy. Defaults to `'Open Thread'`. */
  deepLinkLabel?: string
}

const NOTIFY_ONLY: MentionsServiceSupport = {
  canReply: [],
  hasFullContext: [],
  showPrefix: true,
  isNotifyOnly: true,
}

const MENTIONS_SUPPORT_BY_SERVICE: Partial<
  Record<Service, MentionsServiceSupport>
> = {
  twitter: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post', 'comment'],
    showPrefix: true,
  },
  bluesky: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post', 'comment'],
    showPrefix: true,
  },
  linkedin: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post', 'comment'],
    showPrefix: true,
  },
  tiktok: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post'],
    showPrefix: true,
  },
  youtube: {
    canReply: [],
    hasFullContext: [],
    showPrefix: true,
    subjectLabels: { post: 'video' },
    deepLinkLabel: 'Go to YouTube',
  },
  mastodon: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post'],
    showPrefix: true,
  },
  instagram: {
    canReply: ['post', 'comment'],
    hasFullContext: ['post', 'comment'],
    showPrefix: true,
  },
  facebook: NOTIFY_ONLY,
}

const getSupport = (service: Service): MentionsServiceSupport | undefined =>
  MENTIONS_SUPPORT_BY_SERVICE[service]

/**
 * Live mentions capability bundle. Pure static config — no state, no
 * backend query. Slotted into `CommunityConfigContext.config.mentions`.
 */
export const MENTIONS_CAPABILITIES: MentionsCapabilityConfig = {
  isSupported: ({ service }) => getSupport(service) !== undefined,
  canReply: ({ service }, mentionType) =>
    getSupport(service)?.canReply.includes(mentionType) ?? false,
  hasFullContext: ({ service }, mentionType) =>
    getSupport(service)?.hasFullContext.includes(mentionType) ?? false,
  isNotifyOnly: ({ service }) => getSupport(service)?.isNotifyOnly ?? false,
  subjectLabel: ({ service }, mentionType) =>
    getSupport(service)?.subjectLabels?.[mentionType] ?? mentionType,
  showPrefix: ({ service }) => getSupport(service)?.showPrefix ?? false,
  deepLinkLabel: ({ service }) =>
    getSupport(service)?.deepLinkLabel ?? 'Open Thread',
}

/** Deny-everything stub for tests/stories that don't exercise mention paths. */
export const NOOP_MENTIONS_CAPABILITIES: MentionsCapabilityConfig = {
  isSupported: () => false,
  canReply: () => false,
  hasFullContext: () => false,
  isNotifyOnly: () => false,
  subjectLabel: (_channel, mentionType) => mentionType,
  showPrefix: () => false,
  deepLinkLabel: () => 'Open Thread',
}
